<script setup>
import 'devextreme-vue/text-area'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { DxList } from 'devextreme-vue'

import * as dataGrid from '@/composables/data-grid'

import { useRegistryStore } from '@/stores/registry.store'
import { usePortalStore } from '@/stores/portal.store'

import { useColumns } from '@/composables/registry/product-document-grid'
import { useMultiSelectFilter, useMultiSelectEditorOptions } from '@/composables/data-grid'

import ReferenceLink from '@/components/shared/ReferenceLink.vue'

const props = defineProps({
  product: {
    type: Object,
    required: true
  }
})

const productId = props.product.id
const gridId = 'document-grid'

const referenceTypes = {
  GENERATING: 'generating',
  GENERATED: 'generated'
}

const registryStore = useRegistryStore()
const portalStore = usePortalStore()

const { gridRef } = dataGrid.useGridRef()

const { applyFilter } = useMultiSelectFilter()

const gridDataSource = dataGrid.useODataSource(registryStore.api.getAbsoluteUrl() + registryStore.api.productDocumentsUrl,
  { filter: ['product_ref', '=', props.product.id] },
)

const mainFilter = ['product_ref', '=', props.product.id]

const columns = useColumns()

const onInitNewRow = (e) => {
  e.data.product_ref = productId
}

const onEditorPreparing = (e) => {
  if (e.parentType === 'filterRow' && e.dataField === 'family') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: registryStore.getLocalizedDocumentFamilies(),
      field: e.dataField,
    })
  }

  if (e.parentType === 'filterRow' && e.dataField === 'type') {
    e.editorName = 'dxDropDownBox'
    e.editorOptions = useMultiSelectEditorOptions({
      dataSource: portalStore.getLocalizedDocumentTypes(),
      field: e.dataField,
    })
  }
}

const gridOptions = dataGrid.useDefaultGridOptions({
  id: gridId,
  dataSource: gridDataSource,
  columns: columns,
  editing: {
    allowUpdating: false,
    allowDeleting: false,
    allowAdding: false
  },
  onEditorPreparing: onEditorPreparing,
  paging: { enabled: false },
  pager: { visible: false },
  columnChooser: { enabled: false },
  onInitNewRow: onInitNewRow,
  height: '100%',
})

const getReferenceLinkPrefix = (data, refType) => {
  if (refType === referenceTypes.GENERATING && data.generating_type != null && (data.generating_series || data.generating_number)) {
    return portalStore.getDocumentTypeNameAlt(data.generating_type)
  }
  if (refType === referenceTypes.GENERATED && data.generated_type != null && (data.generated_series || data.generated_number)) {
    return portalStore.getDocumentTypeNameAlt(data.generated_type)
  }
  return null
}

const getReferenceLinkTarget = (data, refType) => {
  if (refType === referenceTypes.GENERATING && data.generating_type != null) {
    return portalStore.documentTypePageMap[data.generating_type] ?? null
  }
  if (refType === referenceTypes.GENERATED && data.generated_type != null) {
    return portalStore.documentTypePageMap[data.generated_type] ?? null
  }
  return null
}

</script>
<template>
  <dx-data-grid
    ref="gridRef"
    v-bind="gridOptions"
  >
    <template #generatingDocumentRef="{ data: cellInfo }">
      <reference-link
        v-if="getReferenceLinkPrefix(cellInfo.data, referenceTypes.GENERATING)"
        :data="{
          prefix: getReferenceLinkPrefix(cellInfo.data, referenceTypes.GENERATING),
          series: cellInfo.data.generating_series,
          number: cellInfo.data.generating_number
        }"
        :text-fields="['prefix', 'series', 'number']"
        :query-fields="['series', 'number']"
        :target="getReferenceLinkTarget(cellInfo.data, referenceTypes.GENERATING)"
      />
    </template>
    <template #generatedDocumentRef="{ data: cellInfo }">
      <reference-link
        v-if="getReferenceLinkPrefix(cellInfo.data, referenceTypes.GENERATED)"
        :data="{
          prefix: getReferenceLinkPrefix(cellInfo.data, referenceTypes.GENERATED),
          series: cellInfo.data.generated_series,
          number: cellInfo.data.generated_number
        }"
        :text-fields="['prefix', 'series', 'number']"
        :query-fields="['series', 'number']"
        :target="getReferenceLinkTarget(cellInfo.data, referenceTypes.GENERATED)"
      />
    </template>
    <template #multiSelectFilterRef="{ data }">
      <dx-list
        :data-source="data.component.option('dataSource')"
        display-expr="name"
        value-expr="id"
        :show-selection-controls="true"
        selection-mode="multiple"
        :select-by-click="true"
        :show-select-all-checkbox="false"
        @selection-changed="e => {
          const selected = e.component.option('selectedItems').map(item => item.id)
          const field = data.component.option('field')
          data.component.option('value', selected)
          applyFilter(selected, field, gridRef, mainFilter)
        }"
      />
    </template>
  </dx-data-grid>
</template>
<style>
</style>
